import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { venueUrl } from "../../utils/venueUrl";
import { API, useAuth } from "../../App";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { useTranslation } from "react-i18next";
import { Calendar, Heart, Star, User, Settings, MapPin, Clock, Users, Check, X, Award, Gift } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import BookingStatusBadge from "../../components/BookingStatusBadge";
import BookingDateFilter, { getDateRange } from "../../components/BookingDateFilter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { getLocalized } from "../../utils/localized";
import { useFormatDate } from "../../utils/dateFormat";
import CustomerLoyalty from "./CustomerLoyalty";
import CustomerRewards from "./CustomerRewards";

export default function CustomerDashboard() {
  const { t } = useTranslation();
  const location = useLocation();

  const navItems = [
    { path: "/account", label: t("customer_dashboard:my_bookings"), icon: Calendar },
    { path: "/account/favorites", label: t("customer_dashboard:favorites"), icon: Heart },
    { path: "/account/reviews", label: t("customer_dashboard:reviews"), icon: Star },
    { path: "/account/loyalty", label: t("customer_dashboard:loyalty", { defaultValue: "Loyalty" }), icon: Award },
    { path: "/account/rewards", label: t("customer_dashboard:rewards", { defaultValue: "Rewards" }), icon: Gift },
    { path: "/account/profile", label: t("customer_dashboard:profile"), icon: User },
    { path: "/account/settings", label: t("customer_dashboard:settings"), icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="lg:w-64 shrink-0">
              <nav className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path || 
                    (item.path === "/account" && location.pathname === "/account/bookings");
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive 
                          ? "bg-primary text-primary-foreground" 
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                    >
                      <Icon className="h-5 w-5" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              <Routes>
                <Route index element={<BookingsPage />} />
                <Route path="bookings" element={<BookingsPage />} />
                <Route path="favorites" element={<FavoritesPage />} />
                <Route path="reviews" element={<ReviewsPage />} />
                <Route path="loyalty" element={<CustomerLoyalty />} />
                <Route path="rewards" element={<CustomerRewards />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Routes>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}

function BookingsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const fmt = useFormatDate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await axios.get(`${API}/bookings`);
        setBookings(response.data);
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  const filteredBookings = bookings.filter((b) => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    const { date_from, date_to } = getDateRange(dateFilter);
    const d = b.booking_date;
    if (date_from && d < date_from) return false;
    if (date_to && d > date_to) return false;
    return true;
  });

  const handleAcceptProposal = async (bookingId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/accept-proposal`);
      toast.success("Proposal accepted!");
      // Refresh bookings
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to accept proposal");
    }
  };

  const handleDeclineProposal = async (bookingId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/decline-proposal`);
      toast.success("Proposal declined");
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to decline proposal");
    }
  };

  const TERMINAL_STATUSES = [
    "completed",
    "rejected",
    "cancelled_by_customer",
    "cancelled_by_venue",
    "canceled_by_user",
    "canceled_by_venue",
    "no_show",
  ];

  const handleCancel = async (bookingId) => {
    if (
      !window.confirm(
        t("customer_dashboard:confirm_cancel", {
          defaultValue: "Cancel this booking? This cannot be undone.",
        })
      )
    ) {
      return;
    }
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, {
        status: "cancelled_by_customer",
      });
      toast.success(
        t("customer_dashboard:cancel_success", { defaultValue: "Booking cancelled" })
      );
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      toast.error(
        t("customer_dashboard:cancel_failed", {
          defaultValue: "Failed to cancel booking",
        })
      );
    }
  };

  const getStatusBadge = (status) => <BookingStatusBadge status={status} />;

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse bg-muted h-32 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="customer-bookings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-medium text-foreground">{t("customer_dashboard:my_bookings")}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <BookingDateFilter value={dateFilter} onChange={setDateFilter} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="customer-booking-status-filter">
              <SelectValue placeholder={t("common:status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common:all")}</SelectItem>
              {["pending","confirmed","change_proposed","completed","rejected","cancelled_by_customer","cancelled_by_venue"].map(s => (
                <SelectItem key={s} value={s}>{t(`booking_status:${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {filteredBookings.length > 0 ? (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <div
              key={booking.booking_id}
              className="bg-card border border-border rounded-xl p-5"
              data-testid={`booking-card-${booking.booking_id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium text-foreground">{getLocalized(booking, "venue_name", lang) || booking.venue_name}</h3>
                    {getStatusBadge(booking.status)}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {fmt(booking.booking_date, "EEE, MMM d, yyyy")}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {booking.booking_time}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {t("booking:guests_count", { count: booking.guest_count })}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {booking.status === "change_proposed" && (
                    <>
                      <div className="text-sm text-muted-foreground mr-4">
                        {t("customer_dashboard:proposed_new_time", { defaultValue: "New time" })}: {booking.proposed_new_date} {t("customer_dashboard:at", { defaultValue: "at" })} {booking.proposed_new_time}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAcceptProposal(booking.booking_id)}
                        data-testid={`accept-proposal-${booking.booking_id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        {t("customer_dashboard:accept", { defaultValue: "Accept" })}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeclineProposal(booking.booking_id)}
                        data-testid={`decline-proposal-${booking.booking_id}`}
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t("customer_dashboard:decline", { defaultValue: "Decline" })}
                      </Button>
                    </>
                  )}
                  {!TERMINAL_STATUSES.includes(booking.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      onClick={() => handleCancel(booking.booking_id)}
                      data-testid={`cancel-booking-${booking.booking_id}`}
                    >
                      <X className="h-4 w-4 mr-1" />
                      {t("customer_dashboard:cancel_booking", { defaultValue: "Cancel" })}
                    </Button>
                  )}
                  <Button asChild variant="outline" size="sm" data-testid={`view-venue-${booking.booking_id}`}>
                    <Link to={venueUrl(booking)}>
                      {t("customer_dashboard:view_venue", { defaultValue: "View Venue" })}
                    </Link>
                  </Button>
                </div>
              </div>

              {booking.venue_response_message && (
                <div className="mt-4 p-3 bg-muted rounded-lg text-sm">
                  <span className="font-medium">{t("customer_dashboard:venue_message", { defaultValue: "Venue message" })}:</span> {booking.venue_response_message}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-muted rounded-xl">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t("customer_dashboard:no_bookings")}</h3>
          <p className="text-muted-foreground mb-4">{t("customer_dashboard:no_bookings_sub")}</p>
          <Button asChild>
            <Link to="/venues">{t("customer_dashboard:browse")}</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

function FavoritesPage() {
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const response = await axios.get(`${API}/favorites`);
        setFavorites(response.data);
      } catch (error) {
        console.error("Error fetching favorites:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, []);

  const handleRemove = async (venueId) => {
    try {
      await axios.delete(`${API}/favorites/${venueId}`);
      setFavorites(favorites.filter(v => v.venue_id !== venueId));
      toast.success(t("customer_dashboard:removed_from_favorites"));
    } catch (error) {
      toast.error(t("customer_dashboard:remove_failed"));
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse bg-muted h-48 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="customer-favorites-page">
      <h1 className="text-2xl font-medium text-foreground mb-6">{t("customer_dashboard:favorites")}</h1>

      {favorites.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {favorites.map((venue) => (
            <div
              key={venue.venue_id}
              className="bg-card border border-border rounded-xl overflow-hidden"
              data-testid={`favorite-card-${venue.venue_id}`}
            >
              <Link to={venueUrl(venue)} className="block aspect-video bg-muted">
                {venue.hero_image ? (
                  <img src={venue.hero_image} alt={venue.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <MapPin className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
              </Link>
              <div className="p-4">
                <Link to={venueUrl(venue)} className="font-medium text-foreground hover:text-primary">
                  {venue.name}
                </Link>
                <p className="text-sm text-muted-foreground mt-1">{venue.venue_type_name} • {venue.city_name}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-destructive hover:text-destructive"
                  onClick={() => handleRemove(venue.venue_id)}
                >
                  <Heart className="h-4 w-4 mr-1 fill-current" />
                  {t("customer_dashboard:remove")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-muted rounded-xl">
          <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t("customer_dashboard:no_favorites")}</h3>
          <p className="text-muted-foreground mb-4">{t("customer_dashboard:no_favorites_sub")}</p>
          <Button asChild>
            <Link to="/venues">{t("customer_dashboard:discover_venues")}</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

function ReviewsPage() {
  const { t } = useTranslation();
  return (
    <div data-testid="customer-reviews-page">
      <h1 className="text-2xl font-medium text-foreground mb-6">{t("customer_dashboard:reviews")}</h1>
      <div className="text-center py-12 bg-muted rounded-xl">
        <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">{t("customer_dashboard:no_reviews")}</h3>
        <p className="text-muted-foreground">{t("customer_dashboard:no_reviews_sub")}</p>
      </div>
    </div>
  );
}

function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, setUser } = useAuth();
  const fmt = useFormatDate();
  const [savingLang, setSavingLang] = useState(false);

  const currentLang = user?.preferred_language || i18n.language || "en";

  const handleLangChange = async (val) => {
    if (val === currentLang) return;
    setSavingLang(true);
    try {
      const { data } = await axios.patch(`${API}/auth/me`, { preferred_language: val });
      setUser?.(data);
      i18n.changeLanguage(val);
      toast.success(t("customer_dashboard:language_saved"));
    } catch (e) {
      toast.error(t("customer_dashboard:save_failed"));
    } finally {
      setSavingLang(false);
    }
  };

  return (
    <div data-testid="customer-profile-page">
      <h1 className="text-2xl font-medium text-foreground mb-6">{t("customer_dashboard:profile")}</h1>

      {/* Language Preference - shown at the top */}
      <div className="bg-card border border-border rounded-xl p-6 mb-6" data-testid="language-preference-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-lg font-medium text-foreground">
              {t("customer_dashboard:language_preference")}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("customer_dashboard:language_preference_desc")}
            </p>
          </div>
          <div className="w-full sm:w-56">
            <Select
              value={currentLang}
              onValueChange={handleLangChange}
              disabled={savingLang}
            >
              <SelectTrigger data-testid="language-preference-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en" data-testid="language-option-en">
                  {t("customer_dashboard:english")}
                </SelectItem>
                <SelectItem value="el" data-testid="language-option-el">
                  {t("customer_dashboard:greek")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
            ) : (
              <User className="h-8 w-8 text-primary" />
            )}
          </div>
          <div>
            <h2 className="text-xl font-medium text-foreground">{user?.name}</h2>
            <p className="text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground">{t("customer_dashboard:phone")}</label>
            <p className="text-foreground">{user?.phone || t("customer_dashboard:not_provided")}</p>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">{t("customer_dashboard:member_since")}</label>
            <p className="text-foreground">
              {user?.created_at ? fmt(user.created_at, "MMMM yyyy") : "—"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsPage() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div data-testid="customer-settings-page">
      <h1 className="text-2xl font-medium text-foreground mb-6">{t("customer_dashboard:settings")}</h1>
      
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-medium text-foreground mb-4">{t("customer_dashboard:account_section")}</h2>
          <div className="space-y-4">
            <Button variant="outline" asChild>
              <Link to="/forgot-password">{t("customer_dashboard:change_password")}</Link>
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-medium text-foreground mb-4">{t("customer_dashboard:danger_zone")}</h2>
          <Button variant="destructive" onClick={handleLogout} data-testid="customer-sign-out">
            {t("customer_dashboard:sign_out")}
          </Button>
        </div>
      </div>
    </div>
  );
}
